import React, { useState, useEffect, useRef } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import usePlaceDetails from './ItineraryPage/usePlaceDetails';
import useItinerary from './ItineraryPage/useItinerary';
import MapSection from './ItineraryPage/MapSection';
import ItinerarySection from './ItineraryPage/ItinerarySection';
import ChatBotItinerarySection from './ItineraryPage/ChatBotItinerarySection';
import '../styles/pages/ItineraryPage/ItineraryPageLayout.css';
import '../styles/pages/ItineraryPage/MapStyles.css';
import { Button } from 'react-bootstrap';
import { useAuth } from '../AuthContext';
import { usePlaceContext } from '../components/itinerary/PlaceContext';
import ItineraryHeader from '../components/itinerary/ItineraryHeader';
import { format } from 'date-fns';
import ItinerarySuggestionsContext from '../components/itinerary/ItinerarySuggestionsContext';
import ProfileTravelPreferences from '../components/profile/ProfileTravelPreferences';
import Popup from '../components/common/popup/Popup';
import { Helmet } from 'react-helmet-async';
import { FaMapMarkedAlt } from 'react-icons/fa';

const ItineraryPage = () => {
  const { origin_id: originParam, itinerary_id } = useParams();
  const origin_id = parseInt(originParam, 10);
  const location = useLocation();
  const isEditMode = location.pathname.includes('/edit/');
  const placeDetails = usePlaceDetails(origin_id);
  const { user } = useAuth();
  const { itineraries, selectedItinerary, handleCreateNewItinerary, handleRenameItinerary, handleDeleteItinerary, setSelectedItinerary, handleUpdatePrivacy, isCreatingItinerary, isRenamingItinerary, isDeletingItinerary, isUpdatingPrivacy } = useItinerary(placeDetails);
  const { state, fetchItineraries, fetchItineraryDetails, handleTagsChange } = usePlaceContext();
  const [isSectionOpen, setIsSectionOpen] = useState({ place: true, itinerary: true });
  const [activeSection, setActiveSection] = useState('itinerary');
  const [showMap, setShowMap] = useState(window.innerWidth > 1024);
  const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth <= 1024);
  const [showPreferencesModal, setShowPreferencesModal] = useState(false);
  const lastFetchedItineraryId = useRef(null);
  const fetchAttemptsRef = useRef(0);
  const MAX_FETCH_ATTEMPTS = 3;
  const itineraryRef = useRef(null);
  const mapSectionRef = useRef(null);

  useEffect(() => {
    const handleResize = () => {
      const smallScreen = window.innerWidth <= 1024;
      setIsSmallScreen(smallScreen);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const loadItineraries = async () => {
      if (!placeDetails) return;
      if (!itinerary_id) {
        if (selectedItinerary) setSelectedItinerary(null);
        return;
      }
      if (user && (!state.itinerariesLoaded || !state.itineraries.some(it => it.id === itinerary_id))) {
        if (fetchAttemptsRef.current < MAX_FETCH_ATTEMPTS) {
          fetchAttemptsRef.current += 1;
          try {
            await fetchItineraries(origin_id, true);
          } catch (e) {}
        }
      } else {
        fetchAttemptsRef.current = 0;
      }
    };
    loadItineraries();
  }, [placeDetails, itinerary_id, origin_id, fetchItineraries, setSelectedItinerary, state.itinerariesLoaded, state.itineraries, user]);

  useEffect(() => {
    const loadSelectedItinerary = async () => {
      if (!placeDetails) return;
      if (!itinerary_id) {
        if (selectedItinerary) setSelectedItinerary(null);
        return;
      }
      if (!isEditMode && selectedItinerary && selectedItinerary.id === itinerary_id) return;
      if (lastFetchedItineraryId.current === itinerary_id) return;
      if (!state.itinerariesLoaded) return;
      const itinerary = state.itineraries.find(it => it.id === itinerary_id);
      if (!itinerary) {
        console.warn('Itinerary not found or inaccessible.');
        if (selectedItinerary && selectedItinerary.id !== itinerary_id) setSelectedItinerary(null);
        return;
      }
      const { itinerary: fullItinerary } = await fetchItineraryDetails(itinerary);
      setSelectedItinerary(fullItinerary);
      lastFetchedItineraryId.current = fullItinerary.id;
    };
    loadSelectedItinerary();
  }, [placeDetails, itinerary_id, fetchItineraryDetails, setSelectedItinerary, state.itineraries, state.itinerariesLoaded, selectedItinerary, isEditMode]);

  const handleCreateNewItineraryCheck = () => {
    if (!user) return;
    handleCreateNewItinerary();
  };

  const toggleMap = () => setShowMap(prev => !prev);

  const formattedDate = selectedItinerary?.updated_datetime_utc ? format(new Date(selectedItinerary.updated_datetime_utc), 'do MMMM yyyy') : null;

  const handleItineraryTagsChange = (updatedTags) => {
    if (!selectedItinerary) return;
    handleTagsChange(updatedTags);
    setSelectedItinerary({
      ...selectedItinerary,
      tags: updatedTags.map(tag => {
        const existingTag = selectedItinerary.tags?.find(t => t.name === tag);
        return existingTag ? existingTag : { name: tag };
      })
    });
  };

  useEffect(() => {
    if (isSmallScreen) {
      if (showMap) {
        mapSectionRef.current?.scrollIntoView({ behavior: 'smooth' });
      } else {
        itineraryRef.current?.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }, [showMap, isSmallScreen]);

  return (
    <>
      <Helmet>
        <title>{isEditMode ? `Edit Itinerary - WeiFinders` : `Itinerary - WeiFinders`}</title>
        <meta name="description" content="Create and manage your travel itinerary on WeiFinders." />
        <meta name="robots" content="noindex, nofollow" />
        <meta property="og:title" content={isEditMode ? `Edit Itinerary - WeiFinders` : `Itinerary - WeiFinders`} />
        <meta property="og:description" content="Create and manage your travel itinerary on WeiFinders." />
        <meta property="og:site_name" content="WeiFinders" />
        <meta property="og:type" content="website" />
        <link rel="canonical" href={`https://weifinders.com/itinerary/${originParam}${isEditMode ? `/edit/${itinerary_id}` : `/${itinerary_id}`}`} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@WeiFinders" />
        <meta name="twitter:title" content={isEditMode ? `Edit Itinerary - WeiFinders` : `Itinerary - WeiFinders`} />
        <meta name="twitter:description" content="Create and manage your travel itinerary on WeiFinders." />
        {selectedItinerary && selectedItinerary.thumbnail_url && (
          <>
            <meta property="og:image" content={selectedItinerary.thumbnail_url} />
            <meta name="twitter:image" content={selectedItinerary.thumbnail_url} />
          </>
        )}
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": "${isEditMode ? `Edit Itinerary - WeiFinders` : `Itinerary - WeiFinders`}",
            "url": "https://weifinders.com/itinerary/${originParam}${isEditMode ? `/edit/${itinerary_id}` : `/${itinerary_id}`}",
            "description": "Create and manage your travel itinerary on WeiFinders."
          }
        `}</script>
      </Helmet>
      {(!placeDetails) ? (
        <div>Loading...</div>
      ) : (itinerary_id && !state.itinerariesLoaded) ? (
        <div>Loading itineraries...</div>
      ) : (
        <div className={`itinerary-page ${isSmallScreen && showMap ? 'fullscreen-map-active' : ''}`}>
          <main role="main">
            <div className="content-wrapper">
              <div className={`info-block ${activeSection === 'chatbot' ? 'assistant-active' : ''}`} ref={itineraryRef}>
                {activeSection === 'itinerary' && (
                  <ItineraryHeader
                    onCreateNew={handleCreateNewItineraryCheck}
                    onRenameItinerary={handleRenameItinerary}
                    onDeleteItinerary={handleDeleteItinerary}
                    onUpdatePrivacy={handleUpdatePrivacy}
                    isCreatingItinerary={isCreatingItinerary}
                    isRenamingItinerary={isRenamingItinerary}
                    isDeletingItinerary={isDeletingItinerary}
                    isUpdatingPrivacy={isUpdatingPrivacy}
                    activeSection={activeSection}
                    setActiveSection={setActiveSection}
                    additionalInfoData={{
                      destinationName: placeDetails.name,
                      destinationLink: `/destination/${origin_id}`,
                      authorName: selectedItinerary?.author_name,
                      updatedDate: formattedDate,
                      tags: selectedItinerary?.tags?.map((t) => t.name) ?? [],
                      thumbnail_url: selectedItinerary?.thumbnail_url || '',
                      itineraryId: selectedItinerary?.id
                    }}
                    enableTagSelector={true}
                    onAdditionalTagsChange={handleItineraryTagsChange}
                  />
                )}
                {activeSection === 'chatbot' && (
                  <div className="minimal-header">
                    <Button variant="primary" className="travel-assistant-button" onClick={() => setActiveSection('itinerary')}>
                      <FaMapMarkedAlt /> View Itinerary
                    </Button>
                  </div>
                )}
                {activeSection === 'itinerary' && (
                  <ItinerarySection
                    itineraries={itineraries}
                    selectedItinerary={selectedItinerary}
                    handleCreateNewItinerary={handleCreateNewItineraryCheck}
                    handleRenameItinerary={handleRenameItinerary}
                    handleDeleteItinerary={handleDeleteItinerary}
                    setSelectedItinerary={setSelectedItinerary}
                    isSectionOpen={isSectionOpen}
                    setIsSectionOpen={setIsSectionOpen}
                    handleUpdatePrivacy={handleUpdatePrivacy}
                    isCreatingItinerary={isCreatingItinerary}
                    isRenamingItinerary={isRenamingItinerary}
                    isDeletingItinerary={isDeletingItinerary}
                    isUpdatingPrivacy={isUpdatingPrivacy}
                    toggleMap={toggleMap}
                    isSmallScreen={isSmallScreen}
                    showMap={showMap}
                  />
                )}
                {activeSection === 'chatbot' && (
                  <ChatBotItinerarySection
                    origin_id={origin_id}
                    itinerary_id={itinerary_id}
                    onOpenPreferences={() => setShowPreferencesModal(true)}
                    toggleMapVisibility={toggleMap}
                    isMapVisible={showMap}
                  />
                )}
              </div>
              <MapSection
                ref={mapSectionRef}
                placeDetails={placeDetails}
                toggleMap={toggleMap}
                isSmallScreen={isSmallScreen}
                showMap={showMap}
                activeSection={activeSection}
              />
            </div>
            {showPreferencesModal && (
              <Popup title="Travel Preferences" onCancel={() => setShowPreferencesModal(false)} className="profile-travel-preferences-popup">
                <div style={{ maxHeight: '80vh', overflowY: 'auto' }}>
                  <ProfileTravelPreferences onClose={() => setShowPreferencesModal(false)} isMobile={isSmallScreen} />
                </div>
              </Popup>
            )}
          </main>
        </div>
      )}
    </>
  );
};

export default ItineraryPage;
